/**
 * An agent's protection limits: the five things the enforcement bridge turns
 * into rules, and the words an operator reads while choosing them.
 *
 * <h3>Why these live here</h3>
 *
 * Each value below is a key `ExternalAgentPolicyBridge.materializeEnforcementRules`
 * reads (`:727-...`), and each ladder is ranked by that same service — a cap is
 * rank-expanded into an allowlist (`resolveAllowedDataClassifications`,
 * `resolveAllowedAutonomyValues`), so a classification capped at INTERNAL allows
 * PUBLIC and blocks CONFIDENTIAL. Get the order wrong here and the choice an
 * operator makes is not the rule the gateway enforces.
 *
 * The lists themselves, and their wording, come from `GelPolicyWizard` — the
 * platform's own Build-tab surface for these limits. They are moved rather than
 * copied: Protect asks the same five questions, and two surfaces that word the
 * same cap differently are two products.
 * `protectionLimits.test.js` reads the ladders off the backend service and
 * fails if either drifts.
 */

/**
 * Least → most sensitive. The ORDER is load-bearing: it is the rank the cap is
 * expanded along (`ExternalAgentPolicyBridge.DATA_CLASSIFICATION_LADDER`).
 */
export const DATA_CLASSIFICATIONS = Object.freeze([
  { value: 'PUBLIC', label: 'Public', desc: 'Non-sensitive, publicly available data' },
  { value: 'INTERNAL', label: 'Internal', desc: 'Internal-only, not for external distribution' },
  { value: 'CONFIDENTIAL', label: 'Confidential', desc: 'Sensitive business data, restricted access' },
  { value: 'RESTRICTED', label: 'Restricted', desc: 'Highest sensitivity — PII, PHI, financial data' },
]);

/**
 * How much the agent may do on its own — one choice, two writes.
 *
 * <h3>Why each choice carries two tokens</h3>
 *
 * "Autonomy" is two different things in this platform, with two vocabularies and
 * two ladders:
 *
 * - `value` is the GEL enforcement cap (`maxAutonomyLevel`), whose ladder is
 *   SUPERVISED → SEMI_AUTONOMOUS → FULLY_AUTONOMOUS. It becomes an ENUM rule the
 *   gateway checks on every protected call.
 * - `trustLevel` is the agent's trust ceiling (`PUT /trust/profiles/{id}/autonomy`),
 *   whose ladder is PROBATION → RESTRICTED → SUPERVISED → FULL. It is what
 *   satisfies the Decide → Act gate's `autonomy_ceiling_set`.
 *
 * SUPERVISED appears in both at DIFFERENT heights — the lowest rung of one and
 * the second-highest of the other — so pairing them by name would make the
 * strictest choice RAISE the ceiling. They are paired by rank instead.
 *
 * PROBATION and the trust ladder's lower rungs are deliberately absent: they are
 * punitive states an operator applies to an agent that has misbehaved, not a
 * ceiling its author picks on the way to go-live.
 */
export const AUTONOMY_CHOICES = Object.freeze([
  {
    value: 'SUPERVISED',
    trustLevel: 'RESTRICTED',
    label: 'Supervised',
    desc: 'Every action requires human approval',
  },
  {
    value: 'SEMI_AUTONOMOUS',
    trustLevel: 'SUPERVISED',
    label: 'Semi-Autonomous',
    desc: 'Pre-approved actions run automatically; novel actions need approval',
  },
  {
    value: 'FULLY_AUTONOMOUS',
    trustLevel: 'SELF_AUTONOMOUS',
    label: 'Fully Autonomous',
    desc: 'Agent operates independently within policy bounds',
  },
]);

/**
 * What happens to sensitive data the agent touches — weakest to strongest. One
 * vocabulary shared with the per-key floor and the workflow Protect step, so an
 * operator sees the same choices everywhere.
 */
export const DPG_SCAN_LEVELS = Object.freeze([
  { value: 'MASK', label: 'Mask', desc: 'Partially hides sensitive values (e.g. ****1234)' },
  { value: 'REDACT', label: 'Redact', desc: 'Replaces sensitive values before they are used' },
  { value: 'TOKENIZE', label: 'Tokenize', desc: 'Swaps sensitive values for reversible tokens' },
  { value: 'BLOCK', label: 'Block', desc: 'Stops any request that contains sensitive data' },
]);

/**
 * A stored value — the action vocabulary above, or a legacy
 * Basic/Standard/Enhanced level — resolved to one of the actions, so a
 * previously-saved policy shows a matching choice and converges to the action
 * vocabulary the next time it is saved.
 */
export const dpgStoredToAction = (v) => {
  switch (String(v || '').toUpperCase()) {
    case 'BLOCK': case 'STRICT': case 'ENHANCED': return 'BLOCK';
    case 'TOKENIZE': return 'TOKENIZE';
    case 'MASK': case 'BASIC': return 'MASK';
    case 'REDACT': case 'STANDARD': default: return 'REDACT';
  }
};

/**
 * What an agent gets if its author says nothing — the most restrictive choice on
 * every ladder except the rate limit, which is a throughput ceiling rather than
 * a protection, and is the backend's own default (`rateLimitPerMinute` defaults
 * to 60 where the rule is built).
 *
 * The autonomy default is the FIRST choice for the same reason
 * `ExternalAgentPolicyBridge.resolveAutonomyLevel` returns SUPERVISED when the
 * key is absent: an agent nobody has decided about does not act alone.
 */
export const LIMIT_DEFAULTS = Object.freeze({
  dataClassification: 'INTERNAL',
  maxAutonomyLevel: AUTONOMY_CHOICES[0].value,
  dpgScanLevel: 'REDACT',
  requiredApprovals: 1,
  rateLimitPerMinute: 60,
});

/** The trust ceiling that goes with a GEL autonomy cap. Unknown cap → the strictest. */
export const trustLevelFor = (maxAutonomyLevel) => {
  const found = AUTONOMY_CHOICES.find((c) => c.value === maxAutonomyLevel);
  return (found || AUTONOMY_CHOICES[0]).trustLevel;
};
